import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Paginator } from 'primereact/paginator';
import { classNames } from 'primereact/utils';

import PropTypes from 'prop-types';
import { paginationDetailString } from 'utils/func';

export default function ScientistPagination({ pagination, setPagination, paginationData }) {
  // #region Data
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPageInput, setCurrentPageInput] = useState(1);
  const [pageInputTooltip, setPageInputTooltip] = useState({
    tooltip: t('pagination.tooltip.info'),
    error: false,
  });

  // #endregion Data
  // #region Event
  const onPaginationChange = (e) => {
    const params = {
      first: e.first,
    };
    if (searchParams.get('search')) {
      params.search = searchParams.get('search');
    }
    setSearchParams(params, { replace: true });

    setPagination({ first: e.first, rowsPerPage: e.rows });
  };

  const onPageInputChange = (value) => {
    setCurrentPageInput(value);
  };

  const onPageInputKeyDown = (event, options) => {
    if (event.key === 'Enter') {
      const page = parseInt(currentPageInput, 10);

      if (page < 0 || page > options.totalPages) {
        setPageInputTooltip({
          tooltip: `${t('pagination.tooltip.error')} ${options.totalPages}.`,
          error: true,
        });
      } else {
        const newFirst = currentPageInput ? options.rows * (page - 1) : 0;

        setPagination((p) => ({ ...p, first: newFirst }));
        setPageInputTooltip({
          tooltip: t('pagination.tooltip.info'),
          error: false,
        });
      }
    }
  };

  const currentPageReport = (options) => (
    <span className="mx-3" style={{ color: 'var(--text-color)', userSelect: 'none' }}>
      {t('pagination.goTo')}{' '}
      <InputNumber
        size="2"
        value={currentPageInput}
        tooltip={pageInputTooltip.tooltip}
        onKeyDown={(e) => onPageInputKeyDown(e, options)}
        onChange={(e) => onPageInputChange(e.value)}
        className={classNames('ml-1', { 'p-invalid': pageInputTooltip.error })}
      />
    </span>
  );
  const rowsPerPageDropdown = (options) => {
    const dropdownOptions = [
      { label: 12, value: 12 },
      { label: 24, value: 24 },
      { label: 48, value: 48 },
      { label: 99, value: 99 },
    ];

    return (
      <div>
        <span className="mx-1" style={{ color: 'var(--text-color)', userSelect: 'none' }}>
          {t('pagination.rowsPerPage')}:{' '}
        </span>
        <Dropdown value={options.value} options={dropdownOptions} onChange={options.onChange} />
      </div>
    );
  };
  // #endregion Event

  // #region Render

  const rightContent = (options) => (
    <span>{paginationDetailString(t, options, paginationData, pagination)}</span>
  );

  const template = {
    layout:
      'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown',
    CurrentPageReport: currentPageReport, // use this due to keydown,
    RowsPerPageDropdown: rowsPerPageDropdown,
  };

  return (
    <div className="pt-2 border-top-1 border-primary-100">
      <Paginator
        template={template}
        first={pagination.first}
        rows={pagination.rowsPerPage}
        totalRecords={paginationData.totalRecords}
        onPageChange={onPaginationChange}
        leftContent={<div />}
        rightContent={rightContent}
      />
    </div>
  );
}

ScientistPagination.propTypes = {
  pagination: PropTypes.shape({
    first: PropTypes.number,
    rowsPerPage: PropTypes.number,
  }).isRequired,
  setPagination: PropTypes.func.isRequired,
  paginationData: PropTypes.shape({
    row: PropTypes.number,
    totalRecords: PropTypes.number,
  }),
};

ScientistPagination.defaultProps = {
  paginationData: {
    row: 10,
    totalRecords: 0,
  },
};
